/**
 * Saree Customizer & Add-ons Component
 * Fully responsive native customization, real-time total price calculation & bundle handler
 */
if (!customElements.get('saree-customizer')) {
  class SareeCustomizer extends HTMLElement {
    connectedCallback() {
      this.initElements();
      this.bindEvents();
      this.syncPropertiesToForm();
      this.updateTotalAmount();
    }

    initElements() {
      // Find associated product form
      const productInfo = this.closest('product-info') || document;
      this.productForm = productInfo.querySelector('product-form') || document.querySelector('product-form');
      this.form = this.productForm?.querySelector('form');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.message = this.querySelector('.saree-customizer__message');
      this.checkoutAfterAdd = false;

      // Add to Cart submit button and text
      this.submitButton = this.productForm?.querySelector('[type="submit"]') || document.querySelector('.product-form__submit');
      this.submitButtonText = this.submitButton?.querySelector('span');
      if (this.submitButtonText && !this.submitButtonText.getAttribute('data-original-text')) {
        const rawText = this.submitButtonText.textContent.trim().split('•')[0].trim();
        this.submitButtonText.setAttribute('data-original-text', rawText || 'ADD TO CART');
      }

      // Currency & Base Price
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.currencySymbol = this.dataset.currencySymbol || '₹';

      // Ready-to-wear elements
      this.radioOptions = this.querySelectorAll('.saree-customizer__option-pill');
      this.clearBtn = this.querySelector('.saree-customizer__clear');
      this.measurementsBox = this.querySelector('.saree-customizer__measurements');
      this.measureInputs = this.querySelectorAll('.saree-customizer__measure-input');

      // Guide Modal
      this.guideBtn = this.querySelector('.saree-customizer__guide-btn');
      this.modal = this.querySelector('.saree-customizer-modal');
      this.modalClose = this.querySelector('.saree-customizer-modal__close');

      // Petticoat elements
      this.petticoatCard = this.querySelector('.saree-customizer__petticoat');
      this.petticoatCheckbox = this.querySelector('.saree-customizer__petticoat-checkbox');
      this.petticoatSizesBox = this.querySelector('.saree-customizer__petticoat-sizes');
      this.petticoatSizePills = this.querySelectorAll('.saree-customizer__size-pill');

      // Gifting elements
      this.giftCards = this.querySelectorAll('.saree-customizer__gift-card');

      // Selected state
      this.state = {
        readyToWear: null, // 'free' | 'custom' | null
        readyToWearPrice: 0,
        readyToWearVariantId: null,
        waist: '',
        hip: '',
        length: '',
        petticoat: false,
        petticoatPrice: 0,
        petticoatSize: 'S',
        petticoatVariantId: this.petticoatCheckbox?.dataset.variantId || null,
        gifts: [] // list of active gifts: { title, price, variantId }
      };
    }

    bindEvents() {
      // Radio options (Free Size / Customise)
      this.radioOptions.forEach((pill) => {
        pill.addEventListener('click', (e) => {
          e.preventDefault();
          const value = pill.dataset.value;
          const variantId = pill.dataset.variantId || null;
          const price = parseInt(pill.dataset.price, 10) || 0;

          if (this.state.readyToWear === value) {
            // Clicking again toggles off
            this.clearReadyToWear();
          } else {
            this.setReadyToWear(value, price, variantId);
          }
        });
      });

      // Clear button
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.clearReadyToWear();
        });
      }

      // Measurement inputs
      this.measureInputs.forEach((input) => {
        input.addEventListener('input', () => {
          input.classList.remove('has-error');
          const prop = input.dataset.property;
          if (prop === 'Waist') this.state.waist = input.value.trim();
          if (prop === 'Hip') this.state.hip = input.value.trim();
          if (prop === 'Length') this.state.length = input.value.trim();
          this.syncPropertiesToForm();
        });
      });

      // Guide modal
      if (this.guideBtn && this.modal) {
        this.guideBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openModal();
        });

        if (this.modalClose) {
          this.modalClose.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeModal();
          });
        }

        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.closeModal();
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
            this.closeModal();
          }
        });
      }

      // Petticoat checkbox (Native change event)
      if (this.petticoatCheckbox) {
        this.petticoatCheckbox.addEventListener('change', () => {
          this.state.petticoat = this.petticoatCheckbox.checked;
          this.state.petticoatPrice = parseInt(this.petticoatCheckbox.dataset.price, 10) || 0;

          if (this.petticoatSizesBox) {
            this.petticoatSizesBox.classList.toggle('is-visible', this.state.petticoat);
          }

          if (this.state.petticoat && !this.state.petticoatSize) {
            const firstPill = this.querySelector('.saree-customizer__size-pill');
            if (firstPill) firstPill.click();
          }

          this.updateTotalAmount();
          this.syncPropertiesToForm();
          this.updatePurchaseOptions();
        });
      }

      // Petticoat size pills
      this.petticoatSizePills.forEach((pill) => {
        pill.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.petticoatSizePills.forEach((p) => p.classList.remove('is-selected'));
          pill.classList.add('is-selected');
          this.state.petticoatSize = pill.dataset.size;
          this.state.petticoatVariantId = pill.dataset.variantId || this.petticoatCheckbox?.dataset.variantId || null;
          this.syncPropertiesToForm();
        });
      });

      // Gifting checkboxes (Native change event on each checkbox)
      this.giftCards.forEach((card) => {
        const checkbox = card.querySelector('.saree-customizer__gift-checkbox');
        if (!checkbox) return;

        checkbox.addEventListener('change', () => {
          card.classList.toggle('is-active', checkbox.checked);
          this.updateGiftsState();
          this.updateTotalAmount();
          this.syncPropertiesToForm();
          this.updatePurchaseOptions();
        });
      });

      // Quantity selector listener to update total dynamically
      const qtyInput = this.form?.querySelector('input[name="quantity"]') || document.querySelector('.quantity__input');
      if (qtyInput) {
        qtyInput.addEventListener('change', () => this.updateTotalAmount());
        qtyInput.addEventListener('input', () => this.updateTotalAmount());
      }

      // Listen for Shopify variant change event
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.variant?.price) {
            this.basePrice = event.data.variant.price;
            this.updateTotalAmount();
          }
        });
      }

      // Hook into Product Form Submit for validation and multi-product cart bundle
      if (this.productForm) {
        this.productForm.addEventListener('submit', this.handleFormSubmit.bind(this), true);
      }
    }

    setReadyToWear(type, price, variantId) {
      this.state.readyToWear = type;
      this.state.readyToWearPrice = price;
      this.state.readyToWearVariantId = variantId;

      this.radioOptions.forEach((pill) => {
        const isMatch = pill.dataset.value === type;
        pill.classList.toggle('is-active', isMatch);
        const radio = pill.querySelector('input[type="radio"]');
        if (radio) radio.checked = isMatch;
      });

      if (this.measurementsBox) {
        this.measurementsBox.classList.toggle('is-visible', type === 'custom');
      }

      this.updateTotalAmount();
      this.syncPropertiesToForm();
      this.updatePurchaseOptions();
    }

    clearReadyToWear() {
      this.state.readyToWear = null;
      this.state.readyToWearPrice = 0;
      this.state.readyToWearVariantId = null;
      this.state.waist = '';
      this.state.hip = '';
      this.state.length = '';

      this.radioOptions.forEach((pill) => {
        pill.classList.remove('is-active');
        const radio = pill.querySelector('input[type="radio"]');
        if (radio) radio.checked = false;
      });

      if (this.measurementsBox) {
        this.measurementsBox.classList.remove('is-visible');
      }

      this.measureInputs.forEach((input) => {
        input.value = '';
        input.classList.remove('has-error');
      });

      this.updateTotalAmount();
      this.syncPropertiesToForm();
      this.updatePurchaseOptions();
    }

    updateGiftsState() {
      const activeGifts = [];
      this.giftCards.forEach((card) => {
        const checkbox = card.querySelector('.saree-customizer__gift-checkbox');
        if (checkbox && checkbox.checked) {
          activeGifts.push({
            title: card.dataset.title,
            price: parseInt(card.dataset.price, 10) || 0,
            variantId: card.dataset.variantId || null
          });
        }
      });
      this.state.gifts = activeGifts;
    }

    formatMoney(cents) {
      const rupees = Math.round(cents / 100);
      return `${this.currencySymbol} ${rupees.toLocaleString('en-IN')}`;
    }

    updateTotalAmount() {
      if (!this.submitButtonText) {
        this.submitButton = this.productForm?.querySelector('[type="submit"]') || document.querySelector('.product-form__submit');
        this.submitButtonText = this.submitButton?.querySelector('span');
      }
      if (!this.submitButtonText) return;

      const originalText = this.submitButtonText.getAttribute('data-original-text') || 'ADD TO CART';

      // Don't modify if button is sold out or unavailable
      if (this.submitButton?.disabled && originalText.toLowerCase().includes('sold out')) {
        return;
      }

      // Get quantity
      const qtyInput = this.form?.querySelector('input[name="quantity"]') || document.querySelector('.quantity__input');
      const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

      // Calculate total: (Base Price * Qty) + Addons
      let totalCents = (this.basePrice * qty);

      // Ready to wear
      if (this.state.readyToWear && this.state.readyToWearVariantId) {
        totalCents += (this.state.readyToWearPrice * qty);
      }

      // Petticoat
      if (this.state.petticoat && this.state.petticoatVariantId) {
        totalCents += (this.state.petticoatPrice * qty);
      }

      // Gifts
      this.state.gifts.forEach((gift) => {
        if (gift.variantId) totalCents += (gift.price * qty);
      });

      // Update button text: "ADD TO CART • ₹ 6,399"
      if (totalCents > 0) {
        this.submitButtonText.textContent = `${originalText} • ${this.formatMoney(totalCents)}`;
      } else {
        this.submitButtonText.textContent = originalText;
      }
    }

    getUnconfiguredSelections() {
      const missing = [];

      if (this.state.readyToWear && !this.state.readyToWearVariantId) {
        missing.push(this.state.readyToWear === 'custom' ? 'Customise' : 'Free Size');
      }
      if (this.state.petticoat && !this.state.petticoatVariantId) {
        missing.push('Matching Petticoat');
      }
      this.state.gifts.forEach((gift) => {
        if (!gift.variantId) missing.push(gift.title);
      });

      return missing;
    }

    updatePurchaseOptions() {
      const hasPaidSelection = Boolean(
        this.state.readyToWear || this.state.petticoat || this.state.gifts.length
      );
      const paymentButton = this.productForm?.querySelector('.shopify-payment-button');
      const bundleBuyNowButton = this.getBundleBuyNowButton(paymentButton);
      if (paymentButton) paymentButton.hidden = hasPaidSelection;
      if (bundleBuyNowButton) {
        bundleBuyNowButton.hidden = !hasPaidSelection;
        bundleBuyNowButton.disabled = this.getUnconfiguredSelections().length > 0;
      }

      const missing = this.getUnconfiguredSelections();
      if (!this.message) return;

      if (missing.length) {
        this.message.textContent = this.dataset.designMode === 'true'
          ? `${missing.join(', ')} price checkout me add karne ke liye Theme Editor me iska add-on product select karein.`
          : `${missing.join(', ')} add-on is currently unavailable.`;
        this.message.hidden = false;
      } else {
        this.message.textContent = '';
        this.message.hidden = true;
      }
    }

    getBundleBuyNowButton(paymentButton) {
      if (!paymentButton) return null;

      let button = this.productForm.querySelector('.saree-customizer__bundle-buy-now');
      if (button) return button;

      button = document.createElement('button');
      button.type = 'button';
      button.className = 'shopify-payment-button__button shopify-payment-button__button--unbranded saree-customizer__bundle-buy-now';
      button.textContent = 'Buy it now';
      button.hidden = true;
      button.addEventListener('click', () => {
        if (this.getUnconfiguredSelections().length) {
          this.updatePurchaseOptions();
          return;
        }

        this.checkoutAfterAdd = true;
        this.form?.requestSubmit(this.submitButton);
      });
      paymentButton.insertAdjacentElement('afterend', button);

      return button;
    }

    openModal() {
      if (this.modal) {
        this.modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    }

    closeModal() {
      if (this.modal) {
        this.modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    }

    validateMeasurements() {
      if (this.state.readyToWear !== 'custom') return true;

      let isValid = true;
      let firstInvalidInput = null;

      this.measureInputs.forEach((input) => {
        const val = parseFloat(input.value);
        const min = parseFloat(input.getAttribute('min')) || 0;
        const max = parseFloat(input.getAttribute('max')) || 999;

        if (isNaN(val) || val < min || val > max) {
          input.classList.add('has-error');
          isValid = false;
          if (!firstInvalidInput) firstInvalidInput = input;
        } else {
          input.classList.remove('has-error');
        }
      });

      if (!isValid && firstInvalidInput) {
        firstInvalidInput.focus();
        firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return isValid;
    }

    syncPropertiesToForm() {
      if (!this.form) return;

      const setProperty = (name, value) => {
        let input = this.form.querySelector(`input[name="properties[${name}]"]`);
        if (value) {
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${name}]`;
            this.form.appendChild(input);
          }
          input.value = value;
        } else if (input) {
          input.remove();
        }
      };

      // Ready-to-wear
      if (this.state.readyToWear === 'free') {
        setProperty('Ready to Wear', null);
        setProperty('_Ready to Wear', 'Free Size');
        setProperty('Waist (in)', null);
        setProperty('Hip (in)', null);
        setProperty('Length (in)', null);
      } else if (this.state.readyToWear === 'custom') {
        setProperty('Ready to Wear', null);
        setProperty('_Ready to Wear', 'Customise');
        setProperty('Waist (in)', this.state.waist ? `${this.state.waist}"` : null);
        setProperty('Hip (in)', this.state.hip ? `${this.state.hip}"` : null);
        setProperty('Length (in)', this.state.length ? `${this.state.length}"` : null);
      } else {
        setProperty('Ready to Wear', null);
        setProperty('_Ready to Wear', null);
        setProperty('Waist (in)', null);
        setProperty('Hip (in)', null);
        setProperty('Length (in)', null);
      }

      // Petticoat
      if (this.state.petticoat) {
        setProperty('Matching Petticoat', null);
        setProperty('_Matching Petticoat', `Yes (Size: ${this.state.petticoatSize || 'S'})`);
      } else {
        setProperty('Matching Petticoat', null);
        setProperty('_Matching Petticoat', null);
      }

      // Gifting
      if (this.state.gifts.length > 0) {
        setProperty('Gifting Add-ons', null);
        setProperty('_Gifting Add-ons', this.state.gifts.map((g) => g.title).join(', '));
      } else {
        setProperty('Gifting Add-ons', null);
        setProperty('_Gifting Add-ons', null);
      }
    }

    async handleFormSubmit(evt) {
      if (!this.validateMeasurements()) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }

      const missingSelections = this.getUnconfiguredSelections();
      if (missingSelections.length) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.updatePurchaseOptions();
        this.message?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const mainQtyInput = this.form.querySelector('input[name="quantity"]');
      const mainQty = mainQtyInput ? parseInt(mainQtyInput.value, 10) || 1 : 1;
      const bundleId = `prithviya-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

      const bundleItems = [];

      // Ready to wear separate variant
      if (this.state.readyToWearVariantId) {
        const props = {
          '_is_addon': 'true',
          '_addon_label': this.state.readyToWear === 'custom' ? 'Customise' : 'Free Size',
          '_bundle_parent_id': bundleId,
          'Service': 'Ready to Wear'
        };
        if (this.state.readyToWear === 'custom') {
          if (this.state.waist) props['Waist'] = `${this.state.waist}"`;
          if (this.state.hip) props['Hip'] = `${this.state.hip}"`;
          if (this.state.length) props['Length'] = `${this.state.length}"`;
        }
        bundleItems.push({
          id: Number(this.state.readyToWearVariantId),
          quantity: mainQty,
          properties: props
        });
      }

      // Petticoat separate variant
      if (this.state.petticoat && this.state.petticoatVariantId) {
        bundleItems.push({
          id: Number(this.state.petticoatVariantId),
          quantity: mainQty,
          properties: {
            '_is_addon': 'true',
            '_addon_label': `Matching Petticoat (${this.state.petticoatSize})`,
            '_bundle_parent_id': bundleId,
            'Size': this.state.petticoatSize
          }
        });
      }

      // Gifting separate variants
      this.state.gifts.forEach((gift) => {
        if (gift.variantId) {
          bundleItems.push({
            id: Number(gift.variantId),
            quantity: mainQty,
            properties: {
              '_is_addon': 'true',
              '_addon_label': gift.title,
              '_bundle_parent_id': bundleId,
              '_addon_for': document.title
            }
          });
        }
      });

      if (bundleItems.length > 0) {
        evt.preventDefault();
        evt.stopPropagation();

        const submitBtn = this.productForm.querySelector('[type="submit"]');
        submitBtn?.setAttribute('aria-disabled', 'true');
        submitBtn?.classList.add('loading');

        try {
          const mainVariantInput = this.form.querySelector('input[name="id"]');
          const mainVariantId = mainVariantInput ? Number(mainVariantInput.value) : null;

          const mainProperties = {};
          const propInputs = this.form.querySelectorAll('input[name^="properties["]');
          propInputs.forEach((inp) => {
            const propName = inp.name.replace(/^properties\[/, '').replace(/\]$/, '');
            mainProperties[propName] = inp.value;
          });
          mainProperties._bundle_id = bundleId;

          // Shopify displays the last item first in the cart. Send the main item
          // last so it appears above its compact add-on rows in the drawer.
          const allItems = [
            ...bundleItems,
            {
              id: mainVariantId,
              quantity: mainQty,
              properties: mainProperties
            }
          ];

          const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST' };
          config.headers = {
            ...(config.headers || {}),
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          };

          const payload = { items: allItems };
          if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
            payload.sections = this.cart.getSectionsToRender().map((s) => s.id).join(',');
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement?.(submitBtn);
          }
          config.body = JSON.stringify(payload);

          const response = await fetch(routes.cart_add_url, config);
          const responseData = await response.json();

          if (!response.ok || responseData.status) {
            throw new Error(responseData.description || responseData.message || 'Error adding items to cart');
          }

          if (this.checkoutAfterAdd) {
            window.location.assign('/checkout');
            return;
          }

          if (this.cart && typeof this.cart.renderContents === 'function') {
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(responseData);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'saree-customizer', cartData: responseData });
            }
          } else {
            window.location = routes.cart_url;
          }
        } catch (err) {
          console.error('[SareeCustomizer]', err);
          if (typeof this.productForm.handleErrorMessage === 'function') {
            this.productForm.handleErrorMessage(err.message);
          } else {
            alert(err.message || 'Unable to add items to cart.');
          }
        } finally {
          this.checkoutAfterAdd = false;
          submitBtn?.removeAttribute('aria-disabled');
          submitBtn?.classList.remove('loading');
        }
      }
    }
  }

  customElements.define('saree-customizer', SareeCustomizer);
}
